import { CheckOutlined, CloseOutlined, RedoOutlined, UndoOutlined } from '@ant-design/icons';
import { Checkbox, Modal } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { analytics } from '../../analytics/analytics';
import { AnalyticsEvent } from '../../analytics/analyticsEvent';
import { AIIcon } from '../../assets/CustomIcons/AIIcon';
import { useAIBreakdownChat } from '../../hooks/useAIBreakdownChat';
import { SubtaskSuggestion } from '../../types/AI';
import Task from '../../types/Task';
import { TaskType } from '../../types/TaskType';
import { useThemeStore } from '../../stores/themeStore';
import { BaseButton } from '../UI/BaseButton';
import { AIBreakdownChat } from './AITaskBreakdown/components/AIBreakdownChat';
import { AIBreakdownTable } from './AITaskBreakdown/components/AIBreakdownTable';
import { UnsavedChangesModal } from './UnsavedChangesModal';
import './styles/AITaskBreakdownModal.scss';

interface AITaskBreakdownModalProps {
  open: boolean;
  task: Task;
  onAccept: (selectedSubtasks: SubtaskSuggestion[]) => void;
  onCancel: () => void;
  usersWithAccessToSpace?: Array<{
    id: number;
    fullName?: string | null;
    email: string;
    profilePicturePath?: string | null;
  }>;
}

export const AITaskBreakdownModal: React.FC<AITaskBreakdownModalProps> = ({
  open,
  task,
  onAccept,
  onCancel,
  usersWithAccessToSpace = [],
}) => {
  const resolvedTheme = useThemeStore((s) => s.resolvedTheme);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [localBreakdown, setLocalBreakdown] = useState<SubtaskSuggestion[]>([]);
  const [withDescriptions, setWithDescriptions] = useState<boolean>(true);
  const chatInputFocusTriggerRef = React.useRef<(() => void) | undefined>();
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Undo/Redo history
  const [history, setHistory] = useState<SubtaskSuggestion[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Refs to avoid stale closures
  const historyRef = useRef<SubtaskSuggestion[][]>([]);
  const historyIndexRef = useRef(-1);
  const lastSyncedBreakdownRef = useRef<string>('');

  // Keep refs in sync with state
  useEffect(() => {
    historyRef.current = history;
  }, [history]);

  useEffect(() => {
    historyIndexRef.current = historyIndex;
  }, [historyIndex]);

  const chat = useAIBreakdownChat({
    workspaceId: task.workspaceId,
    taskTitle: task.title,
    taskDescription: task.description,
    onBreakdownComplete: (breakdown, reasoning) => {
      // Auto-select all subtasks when breakdown is loaded
      if (breakdown.length > 0) {
        setSelectedIndices(breakdown.map((_, index) => index));
        setLocalBreakdown(breakdown);
      }
    },
  });

  // Add breakdown state to history
  const addToHistory = useCallback((breakdown: SubtaskSuggestion[]) => {
    setHistory((prev) => {
      // Check if the last entry in current history is the same
      // This catches duplicates from rapid state updates
      const currentLastEntry = prev[prev.length - 1];
      if (currentLastEntry && JSON.stringify(currentLastEntry) === JSON.stringify(breakdown)) {
        return prev; // No change - it's a duplicate
      }

      // Use ref to get current index to avoid stale closure
      const currentIndex = historyIndexRef.current;
      // Remove any states after current index (when making a new change after undo)
      const newHistory = prev.slice(0, currentIndex + 1);

      // Add new state
      newHistory.push(JSON.parse(JSON.stringify(breakdown)) as SubtaskSuggestion[]); // Deep copy
      return newHistory;
    });

    // Increment index - check against actual state to see if we should
    setHistoryIndex((prevIndex) => {
      const currentHistory = historyRef.current;
      const lastEntry = currentHistory[currentHistory.length - 1];

      // Only increment if the last entry is different from what we're adding
      if (lastEntry && JSON.stringify(lastEntry) === JSON.stringify(breakdown)) {
        return prevIndex; // Don't increment - duplicate was caught
      }
      return prevIndex + 1;
    });
  }, []);

  // Sync local breakdown with chat breakdown
  useEffect(() => {
    if (chat.breakdown.length > 0) {
      // Only sync if the breakdown content has actually changed
      const breakdownString = JSON.stringify(chat.breakdown);
      if (breakdownString !== lastSyncedBreakdownRef.current) {
        lastSyncedBreakdownRef.current = breakdownString;

        setSelectedIndices(chat.breakdown.map((_, index) => index));
        setLocalBreakdown(chat.breakdown);

        // If history is empty, initialize with blank state and first breakdown
        if (historyRef.current.length === 0) {
          const breakdownCopy = JSON.parse(JSON.stringify(chat.breakdown)) as SubtaskSuggestion[];
          setHistory([[], breakdownCopy]);
          setHistoryIndex(1);
        } else {
          // Add to history for subsequent breakdowns
          addToHistory(chat.breakdown);
        }
      }
    }
  }, [chat.breakdown, addToHistory]);

  // Reset chat when modal opens
  useEffect(() => {
    if (open) {
      chat.resetChat();
      // Also reset local state
      setSelectedIndices([]);
      setLocalBreakdown([]);
      setHistory([]);
      setHistoryIndex(-1);
      setHasUnsavedChanges(false);
      lastSyncedBreakdownRef.current = '';
    }
  }, [open, chat.resetChat]);

  // Track if there are unsaved changes (breakdown generated but not accepted)
  useEffect(() => {
    const hasChanges = localBreakdown.length > 0 || chat.breakdown.length > 0;
    setHasUnsavedChanges(hasChanges);
  }, [localBreakdown, chat.breakdown]);

  const handleModalClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setShowUnsavedModal(true);
    } else {
      onCancel();
    }
  }, [hasUnsavedChanges, onCancel]);

  const handleAccept = () => {
    const selectedSubtasks = localBreakdown
      .filter((_, index) => selectedIndices.includes(index))
      .map((subtask) => ({
        ...subtask,
        description: withDescriptions ? subtask.description : '',
      }));

    // Track AI completion analytics
    const attempts = chat.messages.filter((msg) => msg.role === 'user').length;
    void analytics.track(AnalyticsEvent.aiCompleted, {
      taskId: task.id,
      feature: 'vibe_breakdown',
      attempts,
    });

    onAccept(selectedSubtasks);
  };

  const handleSaveAndExit = useCallback(() => {
    setShowUnsavedModal(false);
    handleAccept();
  }, [handleAccept]);

  const handleDiscardAndExit = useCallback(() => {
    setShowUnsavedModal(false);
    onCancel();
  }, [onCancel]);

  const handleCancelUnsavedModal = useCallback(() => {
    setShowUnsavedModal(false);
  }, []);

  const handleSubtaskUpdate = useCallback(
    (index: number, updates: Partial<SubtaskSuggestion>, skipHistory = false) => {
      setLocalBreakdown((prev) => {
        const updated = prev.map((subtask, i) => (i === index ? { ...subtask, ...updates } : subtask));
        // Add to history after state update (unless explicitly skipped)
        if (!skipHistory) {
          addToHistory(updated);
        }
        return updated;
      });
    },
    [addToHistory]
  );

  const handleAddSubtask = useCallback(() => {
    setLocalBreakdown((prev) => {
      const newSubtask: SubtaskSuggestion = {
        title: 'New Task',
        description: '',
        order: prev.length,
      };
      const updated = [...prev, newSubtask];
      // Add to history after state update
      addToHistory(updated);
      // Auto-select the new subtask
      setSelectedIndices((prevIndices) => [...prevIndices, prev.length]);
      return updated;
    });
  }, [addToHistory]);

  const handleUndo = useCallback(() => {
    const currentIndex = historyIndexRef.current;
    const currentHistory = historyRef.current;
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setHistoryIndex(newIndex);
      setLocalBreakdown(JSON.parse(JSON.stringify(currentHistory[newIndex])) as SubtaskSuggestion[]);
    }
  }, []);

  const handleRedo = useCallback(() => {
    const currentIndex = historyIndexRef.current;
    const currentHistory = historyRef.current;
    if (currentIndex < currentHistory.length - 1) {
      const newIndex = currentIndex + 1;
      setHistoryIndex(newIndex);
      setLocalBreakdown(JSON.parse(JSON.stringify(currentHistory[newIndex])) as SubtaskSuggestion[]);
    }
  }, []);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const handleSendPrompt = useCallback(
    (prompt: string) => {
      void chat.sendMessage(prompt);
    },
    [chat]
  );

  const hasBreakdown = chat.breakdown.length > 0 || localBreakdown.length > 0;

  return (
    <>
      {/* Unsaved Changes Modal */}
      <UnsavedChangesModal
        open={showUnsavedModal}
        onDiscard={handleDiscardAndExit}
        onCancel={handleCancelUnsavedModal}
        onSave={handleSaveAndExit}
        isSaving={chat.isLoading}
        message="You haven't accepted your breakdown yet. How would you like to proceed?"
      />

      <Modal
        title={
          <div className="flex items-center gap-2">
            {/* Vibe Breakdown Badge */}
            <div
              className="flex items-center gap-1 px-2 py-1 pl-1 rounded cursor-default relative"
              style={{ background: 'linear-gradient(135deg, #7FF5AA 0%, #08CF65 100%)' }}
            >
              <div
                className="absolute inset-0 rounded pointer-events-none"
                style={{ boxShadow: '0 0 0 2px rgba(255, 255, 255, 0.25) inset' }}
              />
              <AIIcon className="text-white" />
              <span className="text-sm font-semibold text-white">Vibe Breakdown</span>
            </div>
          </div>
        }
        open={open}
        onCancel={handleModalClose}
        afterOpenChange={(isOpen) => {
          // Focus chat input after modal opens
          if (isOpen && chatInputFocusTriggerRef.current) {
            chatInputFocusTriggerRef.current();
          }
        }}
        centered
        width={1250}
        closeIcon={<CloseOutlined style={{ fontSize: 16 }} />}
        footer={null}
        styles={{
          header: { padding: '16px 32px 16px', borderBottom: '1px solid var(--color-neutral-300)', marginBottom: 0 },
          body: { padding: 0, height: '674px', display: 'flex', flexDirection: 'column', position: 'relative' },
        }}
        className="[&_.ant-modal-content]:!p-0 ai-task-breakdown-modal"
      >
        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden min-h-0">
          {/* Left Side - Chat */}
          <div
            className="w-[512px] flex-shrink-0 border-r border-neutral-300 flex flex-col overflow-clip"
            style={{
              backgroundImage:
                resolvedTheme === 'dark'
                  ? 'linear-gradient(142.778deg, #0e3a1e 0%, #0a2614 22.273%, rgba(10, 38, 20, 0.2) 48.599%, #0a2614 72.48%, #145229 100%), linear-gradient(90deg, #0e1114 0%, #0e1114 100%)'
                  : 'linear-gradient(142.778deg, #ADFFC9 0%, #E6FFED 22.273%, rgba(230, 255, 237, 0.2) 48.599%, #E6FFED 72.48%, #7FF5AA 100%), linear-gradient(90deg, #FFFFFF 0%, #FFFFFF 100%)',
            }}
          >
            {/* Chat Messages */}
            <div className="flex-1 overflow-hidden">
              <AIBreakdownChat
                messages={chat.messages}
                isLoading={chat.isLoading}
                userInput={chat.userInput}
                onUserInputChange={chat.setUserInput}
                onSendMessage={chat.sendMessage}
                disabled={false}
                onSendPrompt={handleSendPrompt}
                taskType={task.type}
                hasBreakdown={hasBreakdown}
                lastCompletedMode={chat.lastCompletedMode}
                focusTriggerRef={chatInputFocusTriggerRef}
              />
            </div>
          </div>

          {/* Right Side - Breakdown Results */}
          <div className="flex-1 bg-brand-white flex flex-col min-h-0 overflow-hidden">
            {hasBreakdown ? (
              <>
                {/* Table Panel - Scrollable */}
                <div className="flex-1 overflow-x-auto overflow-y-auto min-h-0">
                  <AIBreakdownTable
                    task={task}
                    subtasks={localBreakdown}
                    selectedIndices={selectedIndices}
                    onSelectionChange={setSelectedIndices}
                    onSubtaskUpdate={handleSubtaskUpdate}
                    onAddSubtask={handleAddSubtask}
                    usersWithAccessToSpace={usersWithAccessToSpace}
                    disabled={false}
                    isLoading={chat.isLoading}
                  />
                </div>

                {/* Bottom Actions Bar - Fixed */}
                <div className="border-t border-neutral-300 bg-brand-white px-4 py-2 flex justify-between items-center flex-shrink-0">
                  {/* Left: Undo/Redo Buttons */}
                  <div className="flex gap-2">
                    <BaseButton
                      type="secondary"
                      size="medium"
                      onClick={handleUndo}
                      disabled={!canUndo || chat.isLoading}
                    >
                      <UndoOutlined />
                    </BaseButton>
                    <BaseButton
                      type="secondary"
                      size="medium"
                      onClick={handleRedo}
                      disabled={!canRedo || chat.isLoading}
                    >
                      <RedoOutlined />
                    </BaseButton>
                  </div>

                  {/* Right: Stats and Accept Button */}
                  <div className="flex items-center gap-3">
                    {/* Task Count */}
                    <div className="px-2 py-1 bg-neutral-200 rounded text-neutral-900 text-xs font-semibold leading-tight">
                      Total: {selectedIndices.length}
                    </div>

                    {/* With Descriptions Checkbox */}
                    <Checkbox
                      className="ai-breakdown-checkbox"
                      checked={withDescriptions}
                      onChange={(e) => setWithDescriptions(e.target.checked)}
                    >
                      <span className="text-sm text-neutral-900">With Descriptions</span>
                    </Checkbox>

                    {/* Accept Button */}
                    <BaseButton
                      type="primary"
                      size="large"
                      onClick={handleAccept}
                      disabled={selectedIndices.length === 0 || chat.isLoading}
                    >
                      <CheckOutlined />
                      {chat.isLoading ? 'Generating...' : 'Accept Proposal'}
                    </BaseButton>
                  </div>
                </div>
              </>
            ) : (
              /* Empty State */
              <div className="flex-1 flex items-center justify-center">
                <div className="flex flex-col items-center gap-5 text-center max-w-xs">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center">
                      <AIIcon className="text-taskonioGreen-600 w-6 h-6" />
                    </div>
                  </div>
                  <p className="text-sm font-medium text-neutral-500">
                    {task.type === TaskType.epic ? 'There are no Items yet' : 'There are no Subitems yet'}
                  </p>
                  {!chat.isLoading && (
                    <BaseButton
                      type="secondary"
                      size="large"
                      onClick={() =>
                        handleSendPrompt(
                          task.type === TaskType.epic
                            ? 'Propose Epic breakdown to Items'
                            : 'Propose Item breakdown to Subitems.'
                        )
                      }
                    >
                      <AIIcon className="w-4 h-4" />
                      {task.type === TaskType.epic
                        ? 'Propose Epic breakdown to Items'
                        : 'Propose Item breakdown to Subitems'}
                    </BaseButton>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </Modal>
    </>
  );
};
